import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  Input,
  DestroyRef,
  // inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectorRef, inject } from '@angular/core';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Chart, PieController, ArcElement, BarController, BarElement, Tooltip, Legend, CategoryScale, LinearScale } from 'chart.js';
import { CoreService } from '../../../services/core.service';

Chart.register(PieController, ArcElement, BarController, BarElement, Tooltip, Legend, CategoryScale, LinearScale, ChartDataLabels);

type ApiResponse = {
  status: 'success' | 'error';
  data?: { metrics?: any };
  message?: string;
};

@Component({
  selector: 'app-analytics',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './analytics.component.html',
  styleUrl: './analytics.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AnalyticsComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() companyId!: number;
  @Input() postingQueueId!: number;
  @Input() tag!: string;
  @Input() socialPlatformId?: number | null;
  @Input() contentSiteId?: number | null;
  @Input() period: string = '30d';

  // UI state
  loading = false;
  errorMsg = '';

  // Metrics 
  totalEng = 0;

  engagementRate = 0;
  globalMedian = 0;
  formatAvg = 0;

  deltaGlobal = '—';
  deltaFormat = '—';

  globalBar = 0;
  formatBar = 0;

  activity = 0;
  virality = 0;
  interactions = 0;
  savesRate = 0;
  savesToLikes = 0;

  likesChart = 0;
  commentsChart = 0;
  sharesChart = 0;
  savesChart = 0;
  clicksChart = 0;

  activityBar = 0;
  viralityBar = 0;
  interactionsBar = 0;
  savesRateBar = 0;
  savesToLikesBar = 0;
  likesBar    = 0;
  savesBar    = 0;
  showPieEmpty = false;

  selectedPeriod: '7d' | '30d' | '90d' | '180d' | '365d' = '90d';

  activityLabel: 'High' | 'Avg' | 'Low' | 'None' = 'None';
  viralityLabel: 'High' | 'Avg' | 'Low' | 'None' = 'None';
  interactionsLabel: 'High' | 'Avg' | 'Low' | 'None' = 'None';
  savesRateLabel: 'High' | 'Avg' | 'Low' | 'None' = 'None';
  saveToLikesLabel: 'High' | 'Avg' | 'Low' | 'None' = 'None';

  statusLabel: 'EXCEPTIONAL' | 'GOOD' | 'AVERAGE' | 'LOW' = 'AVERAGE';

  // AI
  aiSummary = '';
  aiActions: string[] = [];

  // Chart
  @ViewChild('metricsPie') metricsPie?: ElementRef<HTMLCanvasElement>;
  private pieChart?: Chart;

  // Local UI state
  aiOpen = false;
  aiLoading = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly coreService = inject(CoreService);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly periodOptions: Array<'7d' | '30d' | '90d' | '180d' | '365d'> = ['7d', '30d', '90d', '180d', '365d'];
  private viewReady = false;
  private rawLikes    = 0;
  private rawComments = 0;
  private rawShares   = 0;
  private rawSaves    = 0;
  private rawClicks   = 0;

  // UI helpers
  get statusPillClass(): string {
    switch (this.statusLabel) {
      case 'EXCEPTIONAL':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'GOOD':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'AVERAGE':
        return 'bg-gray-50 text-gray-700 border border-gray-200';
      case 'LOW':
        return 'bg-rose-50 text-rose-700 border border-rose-200';
      default:
        return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  }

  metricLevelClass(label?: string) {
    const t = (label || '').toLowerCase();
    if (t.includes('high') || t.includes('exceptional')) return 'text-green-600';
    if (t.includes('avg') || t.includes('medium')) return 'text-amber-600';
    return 'text-red-600';
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.tryLoad();
  }

  ngOnChanges(_: SimpleChanges): void {
    this.tryLoad();
  }

  ngOnDestroy(): void {
    this.pieChart?.destroy();
    this.pieChart = undefined;
  }

  // Data loading
  private tryLoad(): void {
    if (!this.viewReady) return;
    if (!this.companyId || !this.postingQueueId || !this.tag) return;

    const payload: any = {
      company_id: this.companyId,
      posting_queue_id: this.postingQueueId,
      tag: this.tag,
      period: this.selectedPeriod,
    };

    if (this.socialPlatformId != null) payload.social_platform_id = this.socialPlatformId;
    if (this.contentSiteId != null) payload.contentSiteId = this.contentSiteId;

    this.fetchMetrics(payload);
  }

  setPeriod(p: string): void {
    this.selectedPeriod = p as '7d' | '30d' | '90d' | '180d' | '365d';
    this.tryLoad();
  }

  private fetchMetrics(payload: any): void {
    this.loading = true;
    this.errorMsg = '';

    this.totalEng = 0;
    this.engagementRate = 0;
    this.globalMedian = 0;
    this.formatAvg = 0;
    this.deltaGlobal = '—';
    this.deltaFormat = '—';
    this.globalBar = 0;
    this.formatBar = 0;
    this.activity = 0;
    this.virality = 0;
    this.interactions = 0;
    this.savesRate = 0;
    this.savesToLikes = 0;
    this.activityBar = 0;
    this.viralityBar = 0;
    this.interactionsBar = 0;
    this.savesRateBar = 0;
    this.savesToLikesBar = 0;
    this.activityLabel = 'None';
    this.viralityLabel = 'None';
    this.interactionsLabel = 'None';
    this.statusLabel = 'AVERAGE';
    this.aiSummary = '';
    this.aiActions = [];

    this.likesChart = 0;
    this.commentsChart = 0;
    this.sharesChart = 0;
    this.savesChart = 0;
    this.clicksChart = 0;

    this.coreService
      .postData('feedsync/preview-post-performance', payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ApiResponse | any) => {
          const m = res?.data?.metrics;
          if (!m) {
            this.loading = false;
            this.errorMsg = res?.message || 'No metrics returned.';
            return;
          }

          // main metrics
          this.totalEng = this.num(m.total_engagement);
          this.engagementRate = this.num(m.engagment_rate.er);

          const median = this.num(m?.engagment_rate?.global_median?.value);

          // Extract fmtValue properly before using it
          const fmtRaw = m?.engagment_rate?.format_effectiveness?.value;
          const fmtValue: number | null = fmtRaw != null ? this.num(fmtRaw) : null;
          const fmtAvg = fmtValue ?? 0;

          this.globalMedian = median;
          this.formatAvg = fmtAvg;

          const dg = this.num(m?.engagment_rate?.global_median?.difference, this.engagementRate - median);
          const df = fmtValue != null ? this.num(m?.engagment_rate?.format_effectiveness?.difference, this.engagementRate - fmtAvg) : 0;

          this.deltaGlobal = this.formatSigned(dg) + 'pp';
          this.deltaFormat = this.formatSigned(df) + 'pp';


          this.globalBar = this.toDeltaBar(dg, median);
          this.formatBar = fmtValue != null ? this.toDeltaBar(df, this.num(fmtValue)) : 50;

          // breakdown
          const breakdown = m?.metric_brakedown ?? {};
          this.virality = this.num(breakdown.virality);
          this.activity = this.num(breakdown.activity);
          this.interactions = this.num(breakdown.interactions);
          this.savesRate = this.num(breakdown.saves_rate);
          this.savesToLikes = this.num(breakdown.saves_to_likes_ratio);

          this.activityBar      = this.clamp(this.activity * 5, 0, 100);
          this.viralityBar      = this.clamp(this.virality * 5, 0, 100);
          this.interactionsBar  = this.clamp(this.interactions * 100, 0, 100);
          this.savesRateBar     = this.clamp(this.savesRate * 100, 0, 100);
          this.savesToLikesBar  = this.clamp(this.savesToLikes * 100, 0, 100);

          // interactions might be ratio 0–1 or already %
          const inter = this.interactions;

          this.activityLabel = this.levelLabel(this.activityBar);
          this.viralityLabel = this.levelLabel(this.viralityBar);
          this.interactionsLabel = this.levelLabel(this.interactionsBar);
          this.savesRateLabel = this.levelLabel(this.savesRateBar);
          this.saveToLikesLabel = this.levelLabel(this.savesToLikesBar);

          // status
          const status = String(m?.engagment_rate?.post_vs_median?.status ?? '').toLowerCase();
          this.statusLabel =
            status.includes('exception') ? 'EXCEPTIONAL'
            : status.includes('above') ? 'GOOD'
            : status.includes('under') ? 'LOW'
            : 'AVERAGE';

          // AI (only if backend provides it)
          this.aiSummary = typeof m.ai_summary === 'string' ? m.ai_summary : '';
          this.aiActions = Array.isArray(m.ai_actions) ? m.ai_actions : [];

          const chartData = m.performance_pulse ?? {}; 
          this.rawLikes    = this.num(chartData.total_likes);
          this.rawComments = this.num(chartData.total_comments);
          this.rawShares   = this.num(chartData.total_shares);
          this.rawSaves    = this.num(chartData.total_saves);
          this.rawClicks   = this.num(chartData.total_clicks);
          const total = this.rawLikes + this.rawComments + this.rawShares + this.rawSaves + this.rawClicks;

          if (total === 0) {
            this.showPieEmpty = true;
          } else {
            this.showPieEmpty    = false;
            this.likesChart    = (this.rawLikes / total) * 100;
            this.commentsChart = (this.rawComments / total) * 100;
            this.sharesChart   = (this.rawShares / total) * 100;
            this.savesChart    = (this.rawSaves / total) * 100;
            this.clicksChart   = (this.rawClicks / total) * 100;
          }

          // redraw chart
          this.loading = false;
          this.cdr.detectChanges(); // forces *ngIf to resolve and canvas to render
          setTimeout(() => this.renderBar(), 50);
        },
        error: (err) => {
          this.loading = false;
          this.errorMsg = err?.error?.message || err?.message || 'Failed to load analytics.';
          console.error('single-post-performance error', err);
        },
      });
  }

  // Chart
  // private renderPie(): void {
  //   if (this.showPieEmpty) return; // 👈 missing guard

  //   const canvas = this.metricsPie?.nativeElement;
  //   if (!canvas) return;

  //   this.pieChart?.destroy();
  //   this.pieChart = undefined;

  //   const ctx = canvas.getContext('2d');
  //   if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);

  //   this.pieChart = new Chart(canvas, {
  //     type: 'pie',
  //     data: {
  //       labels: ['Likes', 'Comments', 'Shares', 'Saves', 'Clicks'],
  //       datasets: [{
  //         data: [this.likesBar, this.interactionsBar, this.viralityBar, this.savesBar, this.activityBar],
  //         backgroundColor: ['#10B981', '#6366F1', '#F59E0B', '#EC4899', '#3B82F6'],
  //         borderColor: '#ffffff',
  //         borderWidth: 2,
  //         hoverOffset: 6,
  //       }],
  //     },
  //     options: {
  //       responsive: false,
  //       animation: false,
  //       plugins: {
  //         legend: { position: 'bottom' },
  //         tooltip: {
  //           enabled: true,
  //           callbacks: {
  //             label: (ctx) => {
  //               // 👇 now uses class properties, not out-of-scope locals
  //               const counts = [this.rawLikes, this.rawComments, this.rawShares, this.rawSaves, this.rawClicks];
  //               return ` ${ctx.label}: ${counts[ctx.dataIndex]}`;
  //             }
  //           }
  //         },
  //       },
  //     },
  //   });
  // }

  private renderBar(): void {
    if (this.showPieEmpty) return;

    const canvas = this.metricsPie?.nativeElement;
    if (!canvas) return;

    this.pieChart?.destroy();
    this.pieChart = undefined;

    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);

    const colors = [
      { from: '#10B981', to: '#6EE7B7' }, // Likes - emerald
      { from: '#6366F1', to: '#A5B4FC' }, // Comments - indigo
      { from: '#F59E0B', to: '#FCD34D' }, // Shares - amber
      { from: '#EC4899', to: '#F9A8D4' }, // Saves - pink
      { from: '#3B82F6', to: '#93C5FD' }, // Clicks - blue
    ];

    const gradients = colors.map(({ from, to }) => {
      const g = ctx!.createLinearGradient(0, 0, 0, 280);
      g.addColorStop(0, from);
      g.addColorStop(1, to);
      return g;
    });

    this.pieChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: ['Likes', 'Comments', 'Shares', 'Saves', 'Clicks'],
        datasets: [{
          data: [this.rawLikes, this.rawComments, this.rawShares, this.rawSaves, this.rawClicks],
          backgroundColor: gradients,
          borderRadius: 8,
          borderSkipped: false,
          barPercentage: 0.8,
          categoryPercentage: 0.9,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600, easing: 'easeOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1f2937',
            padding: 10,
            cornerRadius: 8,
            callbacks: {
              label: (ctx) => ` ${ctx.label}: ${ctx.raw}`,
            }
          },
          datalabels: {
            anchor: 'end',
            align: 'end',
            color: '#6b7280',
            font: { size: 11, weight: 'bold' },
            padding: { bottom: 4 },
            formatter: (value: number) => value > 0 ? value : '',
          } as any,
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { font: { size: 11 }, color: '#9ca3af' },
          },
          y: {
            display: false,
            beginAtZero: true,
            suggestedMax: Math.max(this.rawLikes, this.rawComments, this.rawShares, this.rawSaves, this.rawClicks) * 1.2,
          },
        },
      },
    });
  }
  // AI toggle
  toggleAiInsights(): void {
    this.aiOpen = !this.aiOpen;

    if (this.aiOpen) {
      this.aiLoading = true;
      this.aiSummary = '';
      this.aiActions = [];

      const payload = {
        posting_queue_id: this.postingQueueId,
        company_id: this.companyId,
        metrics: {
          engagement_rate: this.engagementRate,
          global_median: this.globalMedian,
          format_avg: this.formatAvg,
          activity: this.activity,
          virality: this.virality,
          interactions: this.interactions,
          saves_rate: this.savesRate,
          saves_to_likes: this.savesToLikes,
          total_likes: this.rawLikes,
          total_comments: this.rawComments,
          total_shares: this.rawShares,
          total_saves: this.rawSaves,
          total_clicks: this.rawClicks,
        }
      };

      this.coreService
        .postData('feedsync/get-ai-insights', payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            this.aiSummary = res?.data?.summary ?? '';
            this.aiActions = res?.data?.actions ?? [];
            this.aiLoading = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.aiSummary = 'Could not load AI insights.';
            this.aiActions = [];
            this.aiLoading = false;
            this.cdr.detectChanges();
          }
        });
    }
  }

  // Helpers
  private num(v: any, fallback = 0): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }

  private clamp(v: number, min: number, max: number) {
    return Math.max(min, Math.min(max, v));
  }

  private formatSigned(n: number): string {
    const x = Math.round(n * 100) / 100;
    if (x === 0) return '0';
    return (x > 0 ? '+' : '') + x.toString();
  }

  private levelLabel(v: number): 'High' | 'Avg' | 'Low' | 'None' {
    if (!v || v <= 0) return 'None';
    if (v >= 70) return 'High';
    if (v >= 35) return 'Avg';
    return 'Low';
  }

  private toDeltaBar(difference: number, benchmark: number): number {
    if (!benchmark || benchmark <= 0) return 50; // center = neutral
    // Express difference as % of benchmark, then map to 0–100 centered at 50
    const pct = (difference / benchmark) * 100;
    // Cap swing at ±50pp so bar doesn't go fully empty/full on extreme outliers
    return this.clamp(50 + pct, 0, 100);
  }
}
